import { useEffect, useMemo, useState, useRef} from 'react'
import { Switch, Route, Redirect, matchPath, useLocation } from 'react-router-dom';

// Context
import { useGlobalState } from '../global-state';
import { useNavigator } from '.';
import { useAuth } from '../auth'
import { useHead } from '../head'
import decodeMatch from './functions/decode-match'

export const NavigatorRoutes = () => {
	const { routes, activeRoute, serverProps } = useNavigator();
	const location = useLocation();

	const isInitialLocation = useRef(true);
	const [initialRoute, setInitialRoute] = useState(true);

	const flattenRoutes = useMemo(() => {
		const flatRoutes = {};

		for(const routeKey of Object.keys(routes)) {
			const { children, ...parentRoute } = routes[routeKey];

			flatRoutes[routeKey] = {
				...parentRoute,
				exact: children ? true : parentRoute.exact,
			};

			if(children) {
				for(const childKey of Object.keys(children)) {
					const childRoute = children[childKey];
					if(childRoute.path.startsWith(parentRoute.path)) {
						flatRoutes[`${routeKey}.${childKey}`] = childRoute
					}
					else {
						console.log('NONONO', parentRoute, childRoute);
					}
				}
			}
		}
		return flatRoutes;
	}, [routes])

	useEffect(() => {
		if(isInitialLocation.current === true) {
			isInitialLocation.current = false;
		}
		else {
			setInitialRoute(false);
		}
	}, [location])

	if(serverProps.isServer) {
		return (
			<RouteFactory
				key={`server-${activeRoute.key}`}
				route={activeRoute}
				routeKey={activeRoute.key}
			/>
		)
	}
	else {
		return (
			<Switch>
				{Object.keys(flattenRoutes).map(routeKey => {
					const route = flattenRoutes[routeKey];
					return (
						<Route
							key={routeKey}
							path={route.path}
							exact={route.exact}>
								<RouteFactory
									route={route}
									initialRoute={initialRoute}
									routeKey={routeKey}
								/>
						</Route>
					)
				})}
			</Switch>
		)
	}
}

const RouteFactory = (props) => {
	const { getRoutePath } = useNavigator();
	const redirectPath = useMemo(() => {
		if(props.route.redirect) {
			return getRoutePath(props.route.redirect.key, props.route.redirect.params);
		}
		else {
			return false;
		}
	}, [props.route.redirect])

	if(redirectPath) {
		return <Redirect to={redirectPath} replace={true} />
	}
	else {
		return <RouteRenderer {...props} />;
	}
}

const RouteRenderer = ({ route: propRoute, routeKey, initialRoute }) => {
	const { isNotFound, isError } = useGlobalState();
	const { getRoutePath, PageLoader, onChangeRoute, layouts, serverProps, routes } = useNavigator();
	const { initialized, loggedIn, isGuest } = useAuth();
	const { setBaseHead } = useHead();

	const { loading, route, redirect, LayoutComponent, PageComponent } = useMemo(() => {
		const computedRoute = isError ? routes.error : (isNotFound ? routes.notFound : propRoute);

		const loading = computedRoute.requiresLogin && !initialized;
		return {
			route: computedRoute,
			loading: computedRoute.requiresLogin && !initialized,
			redirect: (!loading && computedRoute.requiresLogin && initialized && (!loggedIn || (isGuest && !computedRoute.acceptsGuestLogin))) ? getRoutePath('login') ?? getRoutePath('notFound') : false,
			LayoutComponent: layouts[computedRoute.layout] ?? layouts['base'],
			PageComponent: computedRoute.component,
		}
	}, [propRoute, initialized, loggedIn, routes, isNotFound, isError])

	const location = useLocation();

	const propMatch = useMemo(() => {
		return decodeMatch(matchPath(
			location.pathname,
			{
				path: propRoute.path
			}
		));
	}, [propRoute.path, location])

	const match = useMemo(() => {
		return decodeMatch(matchPath(
			location.pathname,
			{
				path: route.path
			}
		));
	}, [route.path, location])

	useEffect(() => {
		if(!initialRoute) {
			onChangeRoute({
				...propRoute,
				key: routeKey,
				match: propMatch
			})
		}
	}, [initialRoute, propRoute, routeKey, propMatch, onChangeRoute])

	useEffect(() => {
		if(!initialRoute) {
			setBaseHead({
				...route.head || {},
			});
		}
	}, [initialRoute, route])

	return <LayoutComponent loading={loading} {...(route.layoutProps ?? {})} pageClass={route.layoutClass}>
		{loading ?
			<>{PageLoader}</>
			:
			<>
				{redirect ?
					<Redirect to={redirect} replace={true} />
					:
					<PageComponent {...serverProps} match={match} route={route} />
				}	
			</>
		}
	</LayoutComponent>
}